import { AmountDueSummaryResponse } from '@afterpaytouch/portal-api/consumer/amountDueSummary/types'
import { useCallback, useMemo } from 'react'
import {
  PaymentScheduleData,
  useUpcomingPaymentScheduleSelector,
  useGetAmountDueSummaryQuery,
  useGetOrderTransactionsQuery,
  useGetPaymentScheduleQuery,
} from '../../state'
import { hasAnyOpenDispute } from './utils'

export interface IUseUpcomingPaymentEffects {
  data: {
    amountDueData: AmountDueSummaryResponse
    upcomingPaymentScheduleData: PaymentScheduleData[]
  }
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
  refetchUpcomingPayments: () => void
}

export const useUpcomingPaymentEffects = (): IUseUpcomingPaymentEffects => {
  const upcomingPaymentScheduleData = useUpcomingPaymentScheduleSelector()

  const {
    data: getAmountDueSummaryData,
    isSuccess: isGetAmountDueSummarySuccess,
    isLoading: isGetAmountDueSummaryLoading,
    isFetching: isGetAmountDueSummaryFetching,
    isError: isGetAmountDueSummaryError,
    refetch: refetchGetAmountDueSummaryQuery,
  } = useGetAmountDueSummaryQuery()

  const refetchUpcomingPayments = useCallback((): void => {
    if (isGetAmountDueSummaryError) {
      refetchGetAmountDueSummaryQuery()
    }
  }, [isGetAmountDueSummaryError, refetchGetAmountDueSummaryQuery])

  return {
    data: {
      // @ts-expect-error: OPERATION BLEED STOPPER
      amountDueData: getAmountDueSummaryData,
      upcomingPaymentScheduleData,
    },
    isLoading: isGetAmountDueSummaryFetching || isGetAmountDueSummaryLoading,
    isError: isGetAmountDueSummaryError,
    isSuccess: isGetAmountDueSummarySuccess,
    refetchUpcomingPayments,
  }
}

export const useGetHasAnyOpenDispute = (hasAccountLevelPayments: boolean): boolean => {
  const { data: ordersData } = useGetOrderTransactionsQuery({}, { skip: !hasAccountLevelPayments })
  const { data: dueData } = useGetPaymentScheduleQuery({}, { skip: hasAccountLevelPayments })

  const hasDispute = useMemo(() => {
    if (hasAccountLevelPayments) {
      return ordersData?.results?.some((order) => hasAnyOpenDispute(order)) ?? false
    }
    return dueData?.results?.some((due) => hasAnyOpenDispute(due)) ?? false
  }, [ordersData, dueData, hasAccountLevelPayments])

  return hasDispute
}
